import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { scrollToId } from "@/lib/scroll";

const links = [
  { id: "services", label: "Services" },
  { id: "mobilier", label: "Mobilier" },
  { id: "process", label: "Processus" },
  { id: "realisations", label: "Réalisations" },
  { id: "zone", label: "Zone" },
  { id: "devis", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleMobileClick = (id) => (e) => {
    setOpen(false);
    scrollToId(id)(e);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-stone-200/80 bg-white/75 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
        <a
          href="#top"
          data-testid="logo"
          onClick={scrollToId("top")}
          className="flex items-center gap-3"
          translate="no"
        >
          <img
            src={scrolled ? "/logo-sad.svg?v=14" : "/logo-sad-white.svg?v=14"}
            alt="Logo SAD"
            className="h-14 w-auto object-contain transition-all duration-300 sm:h-16"
          />
          <span
            className={`hidden text-[11px] font-medium uppercase tracking-[0.18em] transition-colors sm:inline ${
              scrolled ? "text-stone-600" : "text-stone-100"
            }`}
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Solutions Agencement Décoration
          </span>
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={scrollToId(l.id)}
              data-testid={`nav-${l.id}`}
              className={`text-sm font-medium tracking-wide transition-colors ${
                scrolled
                  ? "text-stone-700 hover:text-orange-800"
                  : "text-stone-50/95 hover:text-orange-200 [text-shadow:_0_1px_2px_rgb(0_0_0_/_30%)]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a href="#devis" onClick={scrollToId("devis")} className="hidden md:inline-flex">
          <Button
            data-testid="header-cta-quote"
            className={`rounded-full px-5 transition-colors ${
              scrolled
                ? "bg-stone-900 text-stone-50 hover:bg-orange-800"
                : "bg-stone-50 text-stone-900 hover:bg-orange-100"
            }`}
          >
            Parler de votre projet
          </Button>
        </a>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
          className={`rounded-full border p-2 transition-colors md:hidden ${
            scrolled
              ? "border-stone-200 bg-white text-stone-900"
              : "border-stone-50/40 bg-stone-50/10 text-stone-50 backdrop-blur"
          }`}
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="h-4 w-4" strokeWidth={1.5} /> : <Menu className="h-4 w-4" strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-stone-200 bg-white md:hidden">
          <div className="flex flex-col px-6 py-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={handleMobileClick(l.id)}
                className="py-3 text-sm text-stone-700"
              >
                {l.label}
              </a>
            ))}
            <a href="#devis" onClick={handleMobileClick("devis")}>
              <Button className="mt-2 w-full rounded-full bg-stone-900 text-stone-50 hover:bg-orange-800">
                Parler de votre projet
              </Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
